/*
 * vb-virtual-set.js: turns a size-only ring product into a two-band "set"
 * without adding a Set option or a set product.
 *
 * Contract with Liquid (see VIRTUAL-SET.md):
 *   <script type="application/json" data-vb-variants>[{"id":123,"size":"6"}, ...]</script>
 *   <form data-vb-ring-form> ... </form>
 * Emits two line items sharing a hidden _vb_set key so the cart can group them.
 */
(function () {
  'use strict';

  var SET_KEY = '_vb_set';

  var SCRIPT_NAMES = { latin: 'Latin', elder: 'Elder Futhark', younger: 'Younger Futhark', anglo: 'Anglo-Saxon' };

  function json(sel, root) {
    var el = (root || document).querySelector(sel);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /* PUB_SUB_EVENTS is `const` in assets/constants.js. A const in a classic script lives in
     the global LEXICAL scope and is NOT a property of window, so window.PUB_SUB_EVENTS is
     undefined and a guard written that way silently never fires. Reference it bare. */
  function pub(cartData) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'vb-virtual-set', cartData: cartData });
    }
  }

  function newSetId() {
    return 'set-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7);
  }

  function VikingRingForm(form) {
    this.form = form;
    this.variants = json('[data-vb-variants]', form) || [];
    /* The mobile sticky button is OUTSIDE the form (it submits via form="..."), so a
       form-scoped query only ever found the desktop one and the mobile button was never
       disabled during the request. A double tap then posted two sets, four rings. */
    var scope = form.closest('[data-vbr-root]') || document;
    this.buttons = Array.prototype.slice.call(scope.querySelectorAll('[data-vb-atc]'));
    this.errors = Array.prototype.slice.call(scope.querySelectorAll('[data-vb-error]'));
    this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  VikingRingForm.prototype.variantFor = function (size) {
    var hit = this.variants.filter(function (v) { return String(v.size) === String(size); })[0];
    return hit ? hit.id : null;
  };

  VikingRingForm.prototype.field = function (name) {
    var el = this.form.querySelector('[name="' + name + '"]');
    if (!el) return '';
    if (el.type === 'checkbox') return el.checked;
    return el.value;
  };

  /* One band -> one line item. Size lives in the variant; everything else is a property. */
  VikingRingForm.prototype.band = function (index, setId, isSet) {
    var size = this.field(isSet ? 'size_' + index : 'size_1');
    var id = this.variantFor(size);
    if (!id) return null;

    var props = {};
    if (this.field('engrave_on')) {
      var text = this.field(isSet ? 'engraving_' + index : 'engraving_1');
      if (text) {
        props['Engraving'] = text;
        /* The script chips post latin / elder / younger / anglo (vb-ring.js). This used to test
           for 'runic', a value no chip sends, so every rune engraving reached the smith as Latin. */
        var script = this.field('script') || 'latin';
        if (script === 'runic') script = 'elder';
        props['Script'] = SCRIPT_NAMES[script] || 'Latin';
        if (script !== 'latin' && window.VBRunes && window.VBRunes.transliterate) {
          props['Engraving runes'] = window.VBRunes.transliterate(String(text), script);
        }
      }
    }
    /* A CUSTOM variant carries no size, so the real size must ride on the line item.
       An order note would be optional for the customer and easy for fulfilment to miss,
       and this is a made-to-order ring that cannot be cut without it. */
    if (String(size).toUpperCase().indexOf('CUSTOM') !== -1) {
      var custom = this.field(isSet ? 'custom_size_' + index : 'custom_size_1');
      if (!custom) { this.missingCustom = true; return null; }
      props['Custom size'] = custom;
    }

    var date = this.field('wedding_date');
    if (date) props['Wedding date'] = date;

    if (isSet) {
      /* LOAD-BEARING. Shopify merges two line items when the variant id and EVERY property
         match. Two bands of the same size with the same engraving differ only here, so this
         label is what keeps them as two lines. Do not make it uniform and do not rename it
         to an underscore key "for tidiness": the set would collapse to one line of qty 2,
         fulfilment would get one row where it expects two, and the discount would still fire,
         so the failure would be silent. _vb_set_index below is the belt to this braces. */
      props['Part of set'] = 'Band ' + index + ' of 2';
      props[SET_KEY] = setId;          // underscore-prefixed: hidden from the customer
      props['_vb_set_index'] = String(index);
    }
    return { id: id, quantity: 1, properties: props };
  };

  VikingRingForm.prototype.onSubmit = function (e) {
    e.preventDefault();
    this.missingCustom = false;
    var isSet = this.field('pair_mode') === 'set';
    var setId = isSet ? newSetId() : null;
    var items = [];

    if (isSet) {
      var a = this.band(1, setId, true);
      var b = this.band(2, setId, true);
      if (!a || !b) return this.fail(this.missingCustom
        ? 'Add the custom size for each band you set to Custom.'
        : 'Pick a size for both bands.');
      items.push(a, b);
    } else {
      var one = this.band(1, null, false);
      if (!one) return this.fail(this.missingCustom
        ? 'Add your custom size.'
        : 'Pick a size.');
      items.push(one);
    }

    /* Engraving is a separate hidden product so it can be priced without doubling the
       variant count. VIRTUAL-SET.md defines TWO variants, Single and Pair, so a set is
       one Pair unit rather than two Single units. The handoff's own example JSON posted
       quantity 2 against one id, which contradicts its own variant table: with the Single
       id a pair costs 2x$20 instead of $30, with the Pair id a single band costs $30. */
    if (this.field('engrave_on')) {
      /* Count the bands that actually carry text. Charging for two engravings when the
         customer only filled band one is the same class of error in the other direction. */
      var engraved = items.filter(function (it) { return it.properties && it.properties['Engraving']; }).length;

      /* engrave_on gates the CHARGE, the text gates the INSTRUCTION. When they disagreed the
         customer paid for a blank engraving, or paid twice for one engraved band. */
      if (engraved === 0) {
        return this.fail('Add the engraving text, or switch engraving off.');
      }

      var maxLen = Number(this.form.getAttribute('data-vb-max')) || 0;
      if (maxLen) {
        var tooLong = items.some(function (it) {
          return it.properties && it.properties['Engraving'] && it.properties['Engraving'].length > maxLen;
        });
        if (tooLong) return this.fail('That engraving is too long for this band width.');
      }

      {
        var pairId = Number(this.form.getAttribute('data-vb-engraving-variant-pair')) || 0;
        var singleId = Number(this.form.getAttribute('data-vb-engraving-variant-single')) || 0;
        var usePair = isSet && engraved === 2 && pairId;
        var engraveId = usePair ? pairId : singleId;
        var qty = usePair ? 1 : engraved;

        /* Never post engraving instructions without the paid line. Silently skipping it
           sends the order to production with an engraving to cut and nothing collected. */
        if (!engraveId) {
          return this.fail('Engraving is not configured on this store yet. Remove the engraving or contact us to order it.');
        }
        var props = {};
        if (setId) props[SET_KEY] = setId;
        items.push({ id: engraveId, quantity: qty, properties: props });
      }
    }

    this.submit(items);
  };

  VikingRingForm.prototype.submit = function (items) {
    var btns = this.buttons;
    btns.forEach(function (b) { b.setAttribute('aria-busy', 'true'); b.disabled = true; });

    var drawer = document.querySelector('cart-drawer');
    if (drawer && drawer.setActiveElement) drawer.setActiveElement(document.activeElement);
    var body = { items: items };

    /* FIX vs the handoff bundle: it re-fetched cart.js and read `cart.sections`,
       which cart.js never returns, so renderContents() got {} and the drawer never
       updated. Dawn's own product-form.js asks for the sections in the add request
       and hands the whole response to renderContents. Match that. */
    if (drawer && drawer.getSectionsToRender) {
      body.sections = drawer.getSectionsToRender().map(function (s) { return s.id; });
      body.sections_url = window.location.pathname;
    }

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status) throw new Error(data.description || data.message);
        this.errors.forEach(function (el) { el.hidden = true; });
        document.dispatchEvent(new CustomEvent('vb:cart:added', { detail: data }));
        if (drawer && drawer.renderContents) {
          drawer.classList.remove('is-empty');
          /* renderContents queues its own open(); calling open() again registers a second
             transitionend/trapFocus pair. Let it open itself. */
          drawer.renderContents(data);
        }
        pub(data);
      }.bind(this))
      .catch(function (err) { this.fail(err.message || 'Could not add to cart.'); }.bind(this))
      .finally(function () {
        btns.forEach(function (b) { b.removeAttribute('aria-busy'); b.disabled = false; });
      });
  };

  VikingRingForm.prototype.fail = function (message) {
    /* Every slot, not just the first. The only one inside the form sits in a wrapper that
       is display:none below 760px, so on mobile the message was written somewhere the
       customer could never see and the tap read as dead. */
    if (this.errors.length) {
      this.errors.forEach(function (el) { el.textContent = message; el.hidden = false; });
      this.errors[0].scrollIntoView({ block: 'nearest' });
    } else {
      console.warn('[vb-virtual-set]', message);
    }
  };

  /* ---- Cart grouping -------------------------------------------------
     Two line items sharing a _vb_set value are one purchase to the customer.
     Collapse them visually and make either Remove button drop the whole set. */

  function groupCart(root) {
    var rows = Array.prototype.slice.call((root || document).querySelectorAll('[data-vb-line][data-vb-set]'));
    var seen = {};
    rows.forEach(function (row) {
      var key = row.getAttribute('data-vb-set');
      if (seen[key]) { row.setAttribute('data-vb-set-continuation', ''); }
      else { seen[key] = true; row.setAttribute('data-vb-set-lead', ''); }
    });
  }

  function removeSet(setId) {
    var drawer = document.querySelector('cart-drawer');
    return fetch(window.Shopify.routes.root + 'cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var updates = {};
        cart.items.forEach(function (item) {
          if (item.properties && item.properties[SET_KEY] === setId) updates[item.key] = 0;
        });
        /* Ask for the sections and re-render in place. A full page reload in a drawer cart
           throws away the drawer, the scroll position and focus. */
        var body = { updates: updates };
        if (drawer && drawer.getSectionsToRender) {
          body.sections = drawer.getSectionsToRender().map(function (s) { return s.id; });
          body.sections_url = window.location.pathname;
        }
        return fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
          body: JSON.stringify(body)
        });
      })
      .then(function (r) { return r.json(); })
      .then(function (state) {
        if (drawer && drawer.renderContents) {
          drawer.classList.toggle('is-empty', state.item_count === 0);
          drawer.renderContents(state);
        }
        pub(state);
        groupCart();
      });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-vb-remove-set]');
    if (!btn) return;
    e.preventDefault();
    removeSet(btn.getAttribute('data-vb-remove-set'));
  });

  function init() {
    document.querySelectorAll('[data-vb-ring-form]').forEach(function (f) {
      if (!f.__vbBound) { f.__vbBound = true; new VikingRingForm(f); }
    });
    groupCart();
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('vb:cart:added', function () { setTimeout(groupCart, 60); });
  /* cart.js replaces the drawer's innerHTML on every quantity change and remove, and fires
     none of the three events above, so grouping was lost after the first interaction. */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () { setTimeout(groupCart, 0); });
  }
})();
